import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import {clearDrilledGroupId, setDrilledGroupId} from '@Components/poster-editor/poster-editor-reducer';

export class GroupDrillIn {
  private page: Page;

  public constructor(page: Page) {
    this.page = page;
  }

  public onMouseUp(e: CanvasEvents['mouse:up']): void {
    this.tryDrillIn(e);
    this.enableSubTargetCheck();
  }

  public onSelectionCleared(): void {
    window.PMW.redux.store.dispatch(clearDrilledGroupId(this.page.hashedID));
  }

  public clearDrillInStateIfSelectionLeftDrilledGroup(): void {
    const drilledGroupId = this.getDrilledGroupId();
    if (drilledGroupId === null) {
      return;
    }
    const selected = this.page.activeSelection.getActiveObjects();
    if (selected.length === 1) {
      const item = this.page.getItemForFabricObject(selected[0]);
      if (item?.groupId === drilledGroupId) {
        return;
      }
    }
    window.PMW.redux.store.dispatch(clearDrilledGroupId(this.page.hashedID));
  }

  public isActive(): boolean {
    const drilledGroupId = this.getDrilledGroupId();
    if (drilledGroupId === null) {
      return false;
    }
    const selected = this.page.activeSelection.getActiveObjects();
    if (selected.length !== 1) {
      return false;
    }
    const item = this.page.getItemForFabricObject(selected[0]);
    return item?.groupId === drilledGroupId;
  }

  public getDrilledGroupItem(): {item: ItemType; groupId: string} | undefined {
    const drilledGroupId = this.getDrilledGroupId();
    if (drilledGroupId === null) {
      return undefined;
    }
    const selected = this.page.activeSelection.getActiveObjects();
    if (selected.length !== 1) {
      return undefined;
    }
    const item = this.page.getItemForFabricObject(selected[0]);
    if (item?.groupId !== drilledGroupId) {
      return undefined;
    }
    return {item, groupId: drilledGroupId};
  }

  private getDrilledGroupId(): string | null {
    return window.PMW.redux.store.getState().posterEditor.drilledGroupId[this.page.hashedID] ?? null;
  }

  private tryDrillIn(e: CanvasEvents['mouse:up']): void {
    if (!e.isClick) {
      return;
    }
    if (this.page.longPressInitiated) {
      return;
    }
    const fullGroupId = this.page.items.groups.getExclusivelySelectedGroupId();
    if (!fullGroupId) {
      return;
    }
    const nativeEvent = e.e;
    if (nativeEvent instanceof MouseEvent && nativeEvent.button !== 0) {
      return;
    }
    const subTarget = e.subTargets?.[0];
    if (!subTarget) {
      return;
    }
    const item = this.page.getItemForFabricObject(subTarget);
    if (item?.groupId !== fullGroupId) {
      return;
    }

    window.PMW.redux.store.dispatch(setDrilledGroupId({pageHashedId: this.page.hashedID, groupId: fullGroupId}));
    // setActiveObject (vs selectFabricObjects) goes from one non-empty active to another,
    // firing only selection:updated and skipping the cleared/created pair — so
    // onSelectionCleared doesn't run mid-swap and wipe the latch.
    this.page.activeSelection.setActiveObject(subTarget);
  }

  public enableSubTargetCheck(): void {
    const activeObject = this.page.activeSelection.getActiveObject();
    if (activeObject instanceof ActiveSelection) {
      activeObject.subTargetCheck = this.page.items.groups.isSelectionExactlyOneGroup();
    }
  }
}
